import { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import iconUrl from "./brand/momeaze-icon-256.png";
import wordmarkUrl from "./brand/momeaze-wordmark.png";
import { api, ago, Avatar, can, MeContext, useLoad, useRoute, type Me } from "./lib";
import { BarChart3, Bell, Bot, CalendarDays, ClipboardCheck, Compass, FolderOpen, Handshake, LayoutDashboard, Lightbulb, ListTodo, Megaphone, Mic, Plug, Settings as SettingsIcon, Smartphone, Sun, Wallet, type LucideIcon } from "lucide-react";
import { Agents } from "./agents";
import { SearchBox } from "./search";
import { TwoStepSetup } from "./twostep";
import { Analytics } from "./analytics";
import { BrainDump, QuickCapture } from "./braindump";
import { Marketing } from "./marketing";
import { ConnectionsPage, Overview } from "./overview";
import { Resources } from "./resources";
import { Strategy } from "./strategy";
import { WeeklyReview } from "./review";
import { Finance } from "./finance";
import { Partnerships } from "./partners";
import { NotBuilt } from "./placeholders";
import { Product } from "./product";
import { Settings } from "./settings";
import { Today } from "./today";
import { Calendar } from "./calendar";
import { PR } from "./pr";
import { MyWork, Tasks } from "./work";

// Grouped by how often people reach for it: the daily four first, then the company view, the teams, and admin at the bottom.
type NavItem = { path: string; label: string; title?: string; perm?: string; stage?: number; group: string; area: string; icon: LucideIcon };
const NAV: NavItem[] = [
  { path: "/", label: "Today", group: "", area: "daily", icon: Sun },
  { path: "/calendar", label: "Calendar", group: "", area: "daily", icon: CalendarDays },
  { path: "/tasks", label: "Tasks", title: "Tasks", group: "", area: "daily", icon: ListTodo },
  { path: "/brain-dump", label: "Brain dump", perm: "decisions.decide", group: "", area: "daily", icon: Lightbulb },
  { path: "/strategy", label: "Strategy", title: "Strategy", group: "Company", area: "company", icon: Compass },
  { path: "/overview", label: "Overview", perm: "overview.company", group: "Company", area: "company", icon: LayoutDashboard },
  { path: "/analytics", label: "Analytics", perm: "overview.company", group: "Company", area: "company", icon: BarChart3 },
  { path: "/finance", label: "Finance", perm: "finance.view", group: "Company", area: "finance", icon: Wallet },
  { path: "/review", label: "Weekly review", group: "Company", area: "company", icon: ClipboardCheck },
  { path: "/resources", label: "Resources", perm: "resources.view", group: "Company", area: "company", icon: FolderOpen },
  { path: "/marketing", label: "Marketing", perm: "content.view", group: "Teams", area: "marketing", icon: Megaphone },
  { path: "/pr", label: "PR & Speaking", perm: "pr.view", group: "Teams", area: "pr", icon: Mic },
  { path: "/partnerships", label: "Partnerships", title: "Partnerships & Affiliates", perm: "partners.view", group: "Teams", area: "partners", icon: Handshake },
  { path: "/product", label: "Product", title: "Product & Development", perm: "product.view", group: "Teams", area: "product", icon: Smartphone },
  { path: "/agents", label: "Agents", perm: "users.manage", group: "Admin", area: "admin", icon: Bot },
  { path: "/connections", label: "Connections", perm: "users.manage", group: "Admin", area: "admin", icon: Plug },
  { path: "/settings", label: "Settings", group: "Admin", area: "admin", icon: SettingsIcon },
];

function SignIn({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="auth">
      <form
        className="card"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            const r = await api("/api/login", { method: "POST", body: { email, password, code: needsCode ? code : undefined } });
            if (r.needs_code) setNeedsCode(true);
            else onDone();
          } catch (e: any) {
            setErr(e.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="row">
          <img className="brand-mark lg" src={iconUrl} alt="" />
          <strong>Momeaze Portal</strong>
        </div>
        <h1>Sign in</h1>
        {err && <div className="banner bad">{err}</div>}
        <label className="field">
          Email
          <input className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field">
          Password
          <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        {needsCode && (
          <label className="field">
            6-digit code from your authenticator app
            <input className="input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} style={{ letterSpacing: ".2em", fontSize: 20 }} autoFocus required />
            <span className="muted tiny">Lost your phone? Type one of your recovery codes instead.</span>
          </label>
        )}
        <button className="btn primary" disabled={busy}>
          {busy ? "Signing in…" : needsCode ? "Verify and sign in" : "Sign in"}
        </button>
        <p className="muted tiny" style={{ margin: 0 }}>
          Internal team only. Ask an admin in Settings to add you.
        </p>
      </form>
    </div>
  );
}

function SetPassword({ onDone }: { onDone: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="auth">
      <form
        className="card"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await api("/api/me/password", { method: "POST", body: { current, next } });
            onDone();
          } catch (e: any) {
            setErr(e.message);
          }
        }}
      >
        <h1>Set your password</h1>
        <p className="muted small" style={{ margin: 0 }}>
          You signed in with a one-time password. Choose your own to continue (at least 12 characters).
        </p>
        {err && <div className="banner bad">{err}</div>}
        <label className="field">
          One-time password
          <input className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </label>
        <label className="field">
          New password
          <input className="input" type="password" autoComplete="new-password" minLength={12} value={next} onChange={(e) => setNext(e.target.value)} required />
        </label>
        <button className="btn primary">Save and continue</button>
      </form>
    </div>
  );
}

function Notifications({ me, onRead }: { me: Me; onRead: () => void }) {
  const [open, setOpen] = useState(false);
  const { data, reload } = useLoad<any[]>(open ? "/api/notifications" : null);
  useEffect(() => {
    if (open && me.unread) api("/api/notifications/read", { method: "POST" }).then(onRead);
  }, [open]);
  const href = (n: any) => (n.record_type === "content" ? `#/marketing?content=${n.record_id}` : n.record_type === "task" ? `#/tasks?task=${n.record_id}` : n.record_type === "organization" ? `#/partnerships?org=${n.record_id}` : n.record_type === "work_item" ? `#/product?tab=roadmap&item=${n.record_id}` : n.record_type === "opportunity" ? `#/pr?opp=${n.record_id}` : "#/");
  return (
    <div style={{ position: "relative" }}>
      <button className="btn sm icon-btn" onClick={() => (setOpen(!open), reload())} aria-expanded={open} aria-label={`Notifications${me.unread ? `, ${me.unread} unread` : ""}`} title="Notifications">
        <Bell size={17} strokeWidth={1.9} aria-hidden />
        {me.unread > 0 && <span className="badge dot-badge">{me.unread}</span>}
      </button>
      {open && (
        <div className="card" style={{ position: "absolute", right: 0, top: 40, width: "min(360px, calc(100vw - 32px))", zIndex: 10, boxShadow: "var(--shadow)", padding: 8, maxHeight: 420, overflowY: "auto" }}>
          {!data ? (
            <div className="muted small" style={{ padding: 12 }}>Loading…</div>
          ) : !data.length ? (
            <div className="muted small" style={{ padding: 12 }}>Nothing yet.</div>
          ) : (
            data.map((n) => (
              <a key={n.id} href={href(n)} onClick={() => setOpen(false)} className="item clickable" style={{ padding: "10px 12px", color: "inherit", textDecoration: "none" }}>
                {!n.read_at && <span className="dot" style={{ background: "var(--accent)" }} />}
                <span className="grow small">{n.text}</span>
                <span className="muted tiny">{ago(n.created_at)}</span>
              </a>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<"loading" | "signin" | "password" | "twostep" | "ready">("loading");
  const [toastMsg, setToast] = useState<{ msg: string; bad?: boolean; undo?: () => void } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  const [menu, setMenu] = useState(false);
  const route = useRoute();

  const refreshMe = useCallback(() => {
    api<Me>("/api/me")
      .then((m) => {
        setMe(m);
        setState(m.must_change_password ? "password" : (m as any).needs_2fa ? "twostep" : "ready");
      })
      .catch(() => setState("signin"));
  }, []);
  useEffect(refreshMe, []);
  useEffect(() => {
    const f = (e: Event) => ((e as CustomEvent).detail === 428 ? refreshMe() : setState("signin"));
    window.addEventListener("portal:auth", f);
    return () => window.removeEventListener("portal:auth", f);
  }, []);
  useEffect(() => setMenu(false), [route.path]);
  const toast = useCallback((msg: string, bad?: boolean, undo?: () => void) => {
    setToast({ msg, bad, undo });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), undo ? 8000 : 3500);
  }, []);

  if (state === "loading") return null;
  if (state === "signin") return <SignIn onDone={refreshMe} />;
  if (state === "password" || !me) return <SetPassword onDone={refreshMe} />;
  if (state === "twostep")
    return (
      <MeContext.Provider value={{ me, refreshMe, toast }}>
        <div className="auth">
          <div className="card stack">
            <h1>Turn on two-step sign-in</h1>
            <span className="muted small">Your role needs it before you continue. It takes about a minute.</span>
            <TwoStepSetup onDone={refreshMe} />
          </div>
        </div>
      </MeContext.Provider>
    );

  const nav = NAV.filter((n) => !n.perm || can(me, n.perm));
  // My Work moved into Today; old links still land there.
  const current = nav.find((n) => n.path === (route.path === "/my-work" ? "/" : route.path)) ?? null;
  const path = current?.path ?? "/404";

  let page: React.ReactNode;
  if (path === "/") page = <Today />;
  else if (path === "/overview") page = <Overview />;
  else if (path === "/my-work") page = <MyWork />;
  else if (path === "/tasks") page = <Tasks />;
  else if (path === "/marketing") page = <Marketing />;
  else if (path === "/settings") page = <Settings />;
  else if (path === "/partnerships") page = <Partnerships />;
  else if (path === "/finance") page = <Finance />;
  else if (path === "/product") page = <Product />;
  else if (path === "/pr") page = <PR />;
  else if (path === "/calendar") page = <Calendar />;
  else if (path === "/agents") page = <Agents />;
  else if (path === "/review") page = <WeeklyReview />;
  else if (path === "/resources") page = <Resources />;
  else if (path === "/strategy") page = <Strategy />;
  else if (path === "/connections") page = <ConnectionsPage />;
  else if (path === "/analytics") page = <Analytics />;
  else if (path === "/brain-dump") page = <BrainDump />;
  else if (current?.stage) page = <NotBuilt path={path} />;
  else page = <div className="page"><div className="empty">This page doesn't exist, or you don't have access to it.</div></div>;

  return (
    <MeContext.Provider value={{ me, refreshMe, toast }}>
      <div className="shell">
        {menu && <div className="scrim" onClick={() => setMenu(false)} />}
        <nav className={`side ${menu ? "open" : ""}`} aria-label="Main">
          <div className="brand">
            <img className="brand-mark" src={iconUrl} alt="" />
            <span className="wordmark" role="img" aria-label="Momeaze" style={{ WebkitMaskImage: `url(${wordmarkUrl})`, maskImage: `url(${wordmarkUrl})` }} />
          </div>
          <div className="nav">
            {[...new Set(nav.map((n) => n.group))].map((g) => (
              <div key={g || "daily"} className="nav-group">
                {g && <div className="nav-head">{g}</div>}
                {nav.filter((n) => n.group === g).map((n) => (
                  <a key={n.path} href={"#" + n.path} className={`area-${n.area} ${n.path === path ? "on" : ""}`} onClick={() => setMenu(false)}>
                    <span className="label"><n.icon className="nav-icon" size={17} strokeWidth={1.9} aria-hidden />{n.label}</span>
                    {n.path === "/" && me.open_decisions > 0 && <span className="count" key={me.open_decisions}>{me.open_decisions}</span>}
                    {n.stage && <span className="soon">Stage {n.stage}</span>}
                  </a>
                ))}
              </div>
            ))}
          </div>
          <div className="side-foot">
            <a href="#/settings" className="row" style={{ gap: 10, color: "inherit" }}>
              <Avatar id={me.id} name={me.name} hasPhoto={me.has_photo} size={32} />
              <strong style={{ color: "var(--ink)" }}>{me.name}</strong>
            </a>
            <span>{me.role_name}</span>
            <button
              className="btn sm"
              style={{ justifySelf: "start", marginTop: 6 }}
              onClick={async () => {
                await api("/api/logout", { method: "POST" });
                setState("signin");
              }}
            >
              Sign out
            </button>
          </div>
        </nav>
        <main className="main">
          <header className={`topbar area-${current?.area ?? "daily"}`}>
            <div className="row">
              <button className="btn sm menu-btn" onClick={() => setMenu(true)} aria-label="Open menu">
                Menu
              </button>
              <h1>{current?.title ?? current?.label ?? "Not found"}</h1>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <SearchBox pages={nav} />
              {can(me, "decisions.decide") && <QuickCapture />}
              <Notifications me={me} onRead={refreshMe} />
            </div>
          </header>
          {page}
        </main>
      </div>
      {toastMsg && (
        <div className={`toast ${toastMsg.bad ? "bad" : ""}`} role="status">
          {toastMsg.msg}
          {toastMsg.undo && <button className="toast-undo" onClick={() => { const u = toastMsg.undo!; setToast(null); u(); }}>Undo</button>}
        </div>
      )}
    </MeContext.Provider>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
